/**
 * Stable identifiers for the configurable action-center tabs. These keys are the source of truth
 * shared between the admin Case Settings UI (which writes the per-tab config) and the case-page
 * action panel (which reads it), so they must never drift. They are persisted as opaque string
 * keys in `CaseSettings.action_center_tabs`.
 *
 * The step-completion ("Update Status") tab and the client-only tabs (Client Survey / Survey /
 * client Update Status) are intentionally excluded — step completion is core workflow and is never
 * hideable nor shown on closed cases.
 */
export enum ActionCenterTabKey {
  SEND_MESSAGE = 'SEND_MESSAGE',
  NEW_COURT_DATE = 'NEW_COURT_DATE',
  ADD_TASK = 'ADD_TASK',
  SAVE_NOTE = 'SAVE_NOTE',
  ADD_BILLABLE_ITEM = 'ADD_BILLABLE_ITEM',
  UPLOAD_FILE = 'UPLOAD_FILE',
  GENERATE_DOCUMENT = 'GENERATE_DOCUMENT',
  EFILE = 'EFILE'
}

export interface ActionCenterTabMeta {
  key: ActionCenterTabKey;
  /** Human-readable label, matches the action-panel tab label. */
  label: string;
  /** Whether the "Hide tab" toggle is offered for this tab (all current tabs are hideable). */
  hideable: boolean;
}

/**
 * Ordered tab metadata, rendered as rows in the admin "Action Center" settings section and used to
 * resolve config in the action panel. Order mirrors the tab order in the action panel template.
 */
export const ACTION_CENTER_TAB_META: ActionCenterTabMeta[] = [
  { key: ActionCenterTabKey.SEND_MESSAGE, label: 'Send Message', hideable: true },
  { key: ActionCenterTabKey.NEW_COURT_DATE, label: 'New Court Date', hideable: true },
  { key: ActionCenterTabKey.ADD_TASK, label: 'Add Task', hideable: true },
  { key: ActionCenterTabKey.SAVE_NOTE, label: 'Save Note', hideable: true },
  { key: ActionCenterTabKey.ADD_BILLABLE_ITEM, label: 'Add Billable Item', hideable: true },
  { key: ActionCenterTabKey.UPLOAD_FILE, label: 'Upload Files', hideable: true },
  { key: ActionCenterTabKey.GENERATE_DOCUMENT, label: 'Generate Document', hideable: true },
  { key: ActionCenterTabKey.EFILE, label: 'E-File', hideable: true }
];
